import React, { useEffect, useState } from "react";
import Layout from "../../../layout/Main/Layout";
import {
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Box,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    LinearProgress
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { del, get, post, postFiles, put } from "../../../config/axios";
import toast from "react-hot-toast";

const HeroSection = () => {
    const [logos, setLogos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [currentLogo, setCurrentLogo] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [logoName, setLogoName] = useState("");

    const handleGetData = async () => {
        try {
            setLoading(true);
            const data = await get(`/api/dashboard/website/brandlogos`);
            const filteredData = data.filter((item, index) => item.section == "hero-section")
            setLogos(filteredData || []);
        } catch (error) {
            console.error("Error fetching logos:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        handleGetData();
    }, []);

    const handleOpenModal = (logo = null) => {
        setCurrentLogo(logo);
        if (logo) {
            setLogoName(logo.name || "");
        } else {
            setLogoName("");
        }
        setLogoFile(null);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setCurrentLogo(null);
        setLogoFile(null);
    };

    const handleLogoFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error("Logo file size should be less than 5MB");
                return;
            }
            setLogoFile(file);
        }
    };

    const handleSubmit = async () => {
        if (!logoFile && !currentLogo) {
            return toast.error("Please select a logo file");
        }
        try {
            setUploading(true);
            let logoUrl = currentLogo?.logo;

            // Upload logo if new file selected
            if (logoFile) {
                const logoFormData = new FormData();
                logoFormData.append("file", logoFile);
                const { data: { url } } = await postFiles("/api/app/user/uploadImage", logoFormData);
                logoUrl = url;
            }

            if (currentLogo) {
                // Update existing logo
                await put(`/api/dashboard/website/brandlogos/${currentLogo._id}`, {
                    name: logoName,
                    logo: logoUrl
                });
                toast.success("Logo updated successfully");
            } else {
                // Create new logo
                await post(`/api/dashboard/website/brandlogos`, {
                    name: logoName,
                    logo: logoUrl,
                    section: "hero-section"
                });
                toast.success("Logo uploaded successfully");
            }

            handleCloseModal();
            await handleGetData();
        } catch (error) {
            console.error("Error uploading logo:", error);
            toast.error("Failed to process logo");
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await del(`/api/dashboard/website/brandlogos/${id}`);
            toast.success("Logo deleted successfully");
            handleGetData();
        } catch (error) {
            console.error("Error deleting logo:", error);
            toast.error("Failed to delete logo");
        }
    };

    return (
        <Layout>
            <div style={{ padding: "1rem" }}>
                <Typography variant="h5" paddingY={2}>
                    Manage About Hero Section
                </Typography>
                {/* <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenModal()}
                    >
                        Add New
                    </Button>
                </Box> */}
                <TableContainer component={Paper} sx={{ maxWidth: "100%", maxHeight: 400, overflow: "auto" }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><b>S. No.</b></TableCell>
                                <TableCell><b>Image Text</b></TableCell>
                                <TableCell><b>Cover Image</b></TableCell>
                                <TableCell><b>Action</b></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : logos?.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        No logos available
                                    </TableCell>
                                </TableRow>
                            ) : (
                                logos?.map((logo, index) => (
                                    <TableRow key={logo._id}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{logo.name || "Untitled"}</TableCell>
                                        <TableCell>
                                            {logo.logo ? (
                                                <img
                                                    src={logo.logo}
                                                    alt="Logo"
                                                    style={{ width: 100, height: 60, objectFit: 'contain', cursor: 'pointer' }}
                                                    onClick={() => window.open(logo.logo, '_blank')}
                                                />
                                            ) : (
                                                "No Logo"
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleOpenModal(logo)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            {/* <IconButton
                                                color="error"
                                                onClick={() => handleDelete(logo._id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton> */}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Add/Edit Logo Modal */}
                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
                    <DialogTitle>
                        {currentLogo ? "Update Content" : "Add New Image & Text"}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 2 }}>
                            <TextField
                                fullWidth
                                label="Text on image"
                                value={logoName}
                                onChange={(e) => setLogoName(e.target.value)}
                                required
                                sx={{ mb: 3 }}
                            />
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Background Image {!currentLogo && "(required)"}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    sx={{ mr: 2 }}
                                >
                                    {logoFile ? "Change Logo" : "Select Image"}
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={handleLogoFileSelect}
                                    />
                                </Button>
                                {logoFile && (
                                    <Typography variant="body2">
                                        {logoFile.name} ({(logoFile.size / (1024 * 1024)).toFixed(2)} MB)
                                    </Typography>
                                )}
                                {currentLogo?.logo && !logoFile && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Current logo will be kept
                                    </Typography>
                                )}
                            </Box>

                            {uploading && <LinearProgress sx={{ mt: 2 }} />}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseModal} disabled={uploading}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={uploading || (!logoFile && !currentLogo) || !logoName}
                            variant="contained"
                        >
                            {uploading ? "Processing..." : "Save"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        </Layout>
    );
};

export default HeroSection;